import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Users, DollarSign, Clock, TrendingUp, CheckCircle, XCircle, AlertTriangle,
  Shield, RefreshCw, Eye, EyeOff, ChevronDown, ChevronUp, Phone, Calendar,
  CreditCard, Wallet, ArrowRightLeft, UserCheck, Activity, Bell, Bitcoin, Building2,
  Copy, Check, MessageCircle, Link as LinkIcon, Key
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = {
  bg: '#131413',
  card: 'rgba(19,20,19,0.95)',
  cardAlt: '#0d0e0d',
  accent: '#9EAFAC',
  accentDark: '#586060',
  border: 'rgba(158,175,172,0.15)',
  gold: '#C9A84C',
  green: '#4ade80',
  red: '#f87171',
  blue: '#60a5fa',
  orange: '#fb923c',
};
const ADMIN_TOKEN_KEY = 'vg_admin_token';
const getAdminHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` });

// ── Componentes reutilizables ────────────────────────────────────────────────

const StatCard = ({ label, value, sub, Icon, accent, alert }) => (
  <div style={{
    background: C.card,
    border: `1px solid ${alert ? 'rgba(251,146,60,0.3)' : C.border}`,
    borderRadius: '16px',
    padding: '20px',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: alert ? '0 0 18px rgba(251,146,60,0.08)' : 'none',
  }}>
    {alert && <div style={{ position: 'absolute', top: 10, right: 10, width: 8, height: 8, borderRadius: '50%', background: C.orange, boxShadow: '0 0 8px rgba(251,146,60,0.7)', animation: 'pulse-dot 1.5s infinite' }} />}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <div style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>{label}</div>
        <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '28px', fontWeight: 900, color: accent || C.accent }}>{value}</div>
        {sub && <div style={{ color: C.accentDark, fontSize: '12px', marginTop: '4px' }}>{sub}</div>}
      </div>
      {Icon && (
        <div style={{ padding: '10px', background: `${accent || C.accent}14`, borderRadius: '10px' }}>
          <Icon size={18} color={accent || C.accent} />
        </div>
      )}
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const cfg = {
    active:     { color: '#9EAFAC', bg: 'rgba(158,175,172,0.1)', label: 'Activo' },
    pending:    { color: '#d97706', bg: 'rgba(217,119,6,0.1)',   label: 'Pendiente' },
    completed:  { color: '#4ade80', bg: 'rgba(74,222,128,0.1)', label: 'Completado' },
    rejected:   { color: '#f87171', bg: 'rgba(248,113,113,0.1)', label: 'Rechazado' },
    processing: { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)', label: 'Procesando' },
    reported:   { color: '#d97706', bg: 'rgba(217,119,6,0.1)',  label: 'Reportado' },
    finished:   { color: '#4ade80', bg: 'rgba(74,222,128,0.1)', label: 'Aprobado' },
    failed:     { color: '#f87171', bg: 'rgba(248,113,113,0.1)', label: 'Fallido' },
  }[status] || { color: C.accentDark, bg: 'rgba(158,175,172,0.06)', label: status };
  return (
    <span style={{ color: cfg.color, background: cfg.bg, borderRadius: '50px', padding: '3px 10px', fontSize: '12px', fontWeight: 700 }}>
      {cfg.label}
    </span>
  );
};

const ActionBtn = ({ onClick, color, bg, border, children }) => (
  <button
    onClick={onClick}
    style={{
      padding: '6px 14px',
      background: bg || 'rgba(158,175,172,0.08)',
      border: `1px solid ${border || C.border}`,
      borderRadius: '8px',
      color: color || C.accent,
      fontSize: '12px',
      fontWeight: 700,
      cursor: 'pointer',
      fontFamily: 'Inter, sans-serif',
      transition: 'all 0.15s',
    }}
  >{children}</button>
);

// ── User Detail Panel ─────────────────────────────────────────────────────────

const money = (value) => `$${parseFloat(value || 0).toFixed(2)}`;
const nodeDepth = (node) => Number(node?.depth ?? node?.referral_depth ?? 0);
const nodeSearchText = (node) => [
  node?.full_name,
  node?.phone_e164,
  node?.email,
  node?.referral_code,
  node?.sponsor_name,
  node?.referred_by_name,
].join(' ').toLowerCase();

const isRootUserCard = (user) => {
  const sponsorName = (user?.sponsor_name || user?.referred_by_name || '').trim().toLowerCase();
  return !user?.referred_by || Number(user?.referral_depth ?? -1) === 0 || sponsorName === 'directo';
};

const TreeNodeCard = ({ node, childrenCount, isMatch, collapsed, onToggle }) => (
  <div style={{
    width: '168px',
    minHeight: '138px',
    background: isMatch ? 'rgba(158,175,172,0.13)' : C.card,
    border: `1px solid ${isMatch ? 'rgba(158,175,172,0.42)' : C.border}`,
    borderRadius: '10px',
    padding: '10px',
    boxShadow: '0 8px 20px rgba(0,0,0,0.16)',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px', alignItems: 'flex-start' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: 'white', fontSize: '12px', fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.full_name || 'Usuario'}</div>
        <div style={{ color: C.accent, fontSize: '11px', fontWeight: 700, marginTop: '1px' }}>{node.referral_code || 'Sin código'}</div>
      </div>
      <div style={{ transform: 'scale(0.82)', transformOrigin: 'top right' }}>
        <StatusBadge status={node.investment_status} />
      </div>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
      <div style={{ background: 'rgba(158,175,172,0.05)', border: `1px solid ${C.border}`, borderRadius: '7px', padding: '4px 5px' }}>
        <div style={{ color: C.accentDark, fontSize: '9px', textTransform: 'uppercase' }}>Nivel</div>
        <div style={{ color: 'white', fontSize: '12px', fontWeight: 700 }}>{nodeDepth(node)}</div>
      </div>
      <div style={{ background: 'rgba(158,175,172,0.05)', border: `1px solid ${C.border}`, borderRadius: '7px', padding: '4px 5px' }}>
        <div style={{ color: C.accentDark, fontSize: '9px', textTransform: 'uppercase' }}>Directos</div>
        <div style={{ color: 'white', fontSize: '12px', fontWeight: 700 }}>{childrenCount}</div>
      </div>
    </div>
    <div style={{ color: C.accentDark, fontSize: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Sponsor: {node.sponsor_name || 'Directo'}</div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', color: C.accentDark, fontSize: '10px' }}>
      <span>{node.matrix_positions_count || 0} posiciones</span>
      <span>{money(node.matrix_gross_volume)} vol.</span>
      <span style={{ color: C.green }}>{money(node.total_earnings)} gan.</span>
      <span>{money(node.organization_generated)} org.</span>
    </div>
    <div style={{ display: 'flex', gap: '5px', marginTop: 'auto' }}>
      <button onClick={() => navigator.clipboard.writeText(node.referral_path || '')} style={{ flex: 1, background: 'rgba(158,175,172,0.08)', border: `1px solid ${C.border}`, borderRadius: '7px', color: C.accent, padding: '5px 7px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '11px', fontWeight: 700 }}>
        <Copy size={11} /> Path
      </button>
      {childrenCount > 0 && (
        <button onClick={onToggle} style={{ background: 'rgba(158,175,172,0.08)', border: `1px solid ${C.border}`, borderRadius: '7px', color: C.accent, padding: '5px 7px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 700 }}>
          {collapsed ? <ChevronDown size={11} /> : <ChevronUp size={11} />}
        </button>
      )}
    </div>
  </div>
);

const GraphicalReferralTree = ({ nodes, users, rootUserId, search, maxDepth, status, onSelectRoot, onLoadUsers }) => {
  const [collapsed, setCollapsed] = useState({});
  const [treeZoom, setTreeZoom] = useState(0.9);
  const searchTerm = (search || '').trim().toLowerCase();
  const limitDepth = Number(maxDepth || 10);

  const prepared = useMemo(() => {
    const source = Array.isArray(nodes) ? nodes : [];
    const byId = new Map(source.map(node => [node.id, { ...node, children: [] }]));
    const roots = [];

    byId.forEach(node => {
      const parent = byId.get(node.referred_by);
      if (parent && node.id !== parent.id) parent.children.push(node);
      else roots.push(node);
    });

    byId.forEach(node => node.children.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '')));
    roots.sort((a, b) => (nodeDepth(a) - nodeDepth(b)) || (a.created_at || '').localeCompare(b.created_at || ''));

    const matches = new Set();
    const visible = new Set();
    const markVisible = (node, ancestors = []) => {
      const ownMatch = !searchTerm || nodeSearchText(node).includes(searchTerm);
      const childMatch = node.children.map(child => markVisible(child, [...ancestors, node])).some(Boolean);
      if (ownMatch) matches.add(node.id);
      if (ownMatch || childMatch) {
        visible.add(node.id);
        ancestors.forEach(parent => visible.add(parent.id));
        return true;
      }
      return false;
    };
    roots.forEach(root => markVisible(root));

    const levels = {};
    source.forEach(node => {
      const depth = nodeDepth(node);
      if (visible.has(node.id) && depth <= limitDepth) levels[depth] = (levels[depth] || 0) + 1;
    });

    return { roots, visible, matches, levels };
  }, [nodes, searchTerm, limitDepth]);

  const renderNode = (node) => {
    const depth = nodeDepth(node);
    if (depth > limitDepth || !prepared.visible.has(node.id)) return null;
    const visibleChildren = node.children.filter(child => prepared.visible.has(child.id) && nodeDepth(child) <= limitDepth);
    const isCollapsed = Boolean(collapsed[node.id]);
    return (
      <div key={node.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
        <TreeNodeCard
          node={node}
          childrenCount={node.children.length || node.direct_active_referrals || 0}
          isMatch={prepared.matches.has(node.id)}
          collapsed={isCollapsed}
          onToggle={() => setCollapsed(prev => ({ ...prev, [node.id]: !prev[node.id] }))}
        />
        {visibleChildren.length > 0 && !isCollapsed && (
          <>
            <div style={{ width: '1px', height: '16px', background: 'rgba(158,175,172,0.25)' }} />
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', gap: '16px', alignItems: 'flex-start' }}>
              <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: `${Math.max(0, (visibleChildren.length - 1) * 184)}px`, height: '1px', background: visibleChildren.length > 1 ? 'rgba(158,175,172,0.25)' : 'transparent' }} />
              {visibleChildren.map(child => (
                <div key={child.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: '1px', height: '16px', background: 'rgba(158,175,172,0.25)' }} />
                  {renderNode(child)}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  if (!rootUserId) {
    const nodeRoots = Array.isArray(nodes) ? nodes : [];
    const userRoots = (Array.isArray(users) ? users : [])
      .filter(isRootUserCard)
      .map(user => ({
        ...user,
        depth: 0,
        referral_depth: 0,
        sponsor_name: user.sponsor_name || (user.referred_by_name === 'Directo' ? null : user.referred_by_name),
        direct_active_referrals: user.direct_active_referrals || 0,
        matrix_positions_count: user.matrix_positions_count || 0,
        matrix_gross_volume: user.matrix_gross_volume || 0,
        organization_generated: user.organization_generated || 0,
      }));
    const rootSource = nodeRoots.length > 0 ? nodeRoots : userRoots;
    const rootCards = rootSource.filter(node => !searchTerm || nodeSearchText(node).includes(searchTerm));
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
        {rootCards.map(root => (
          <div key={root.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div>
              <div style={{ color: 'white', fontWeight: 800, fontSize: '14px' }}>{root.full_name || 'Usuario raíz'}</div>
              <div style={{ color: C.accent, fontSize: '12px', fontWeight: 700, marginTop: '2px' }}>{root.referral_code || 'Sin código'}</div>
            </div>
            <div style={{ color: C.accentDark, fontSize: '12px' }}>{root.direct_active_referrals || 0} directos · {root.matrix_positions_count || 0} posiciones · {money(root.matrix_gross_volume)}</div>
            <button onClick={() => onSelectRoot?.(root.id)} style={{ background: 'rgba(158,175,172,0.1)', border: '1px solid rgba(158,175,172,0.3)', borderRadius: '9px', color: C.accent, padding: '9px 12px', cursor: 'pointer', fontWeight: 800, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}>
              Ver árbol <ChevronDown size={14} />
            </button>
          </div>
        ))}
        {rootCards.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: C.accentDark, gridColumn: '1 / -1', background: 'rgba(158,175,172,0.03)', border: `1px solid ${C.border}`, borderRadius: '12px' }}>
            <div style={{ color: 'white', fontWeight: 800, marginBottom: '6px' }}>No hay usuarios raíz disponibles.</div>
            <div>Verifica que existan usuarios con sponsor directo o referral_depth 0.</div>
            {(!users || users.length === 0) && (
              <button onClick={onLoadUsers} style={{ marginTop: '14px', background: 'rgba(158,175,172,0.08)', border: `1px solid ${C.border}`, borderRadius: '8px', color: C.accent, padding: '8px 12px', cursor: 'pointer', fontWeight: 800 }}>
                Cargar usuarios
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ background: 'rgba(158,175,172,0.03)', border: `1px solid ${C.border}`, borderRadius: '14px', padding: '18px', overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginBottom: '12px', minWidth: '620px' }}>
        {[0.75, 1, 1.25].map(zoom => (
          <button key={zoom} onClick={() => setTreeZoom(zoom)} style={{ background: treeZoom === zoom ? 'rgba(158,175,172,0.16)' : 'rgba(158,175,172,0.06)', border: `1px solid ${treeZoom === zoom ? 'rgba(158,175,172,0.38)' : C.border}`, borderRadius: '8px', color: treeZoom === zoom ? C.accent : C.accentDark, padding: '5px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: 800 }}>
            {Math.round(zoom * 100)}%
          </button>
        ))}
      </div>
      <div style={{ minWidth: '620px', display: 'flex', justifyContent: 'center', padding: '4px 0 18px' }}>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', transform: `scale(${treeZoom})`, transformOrigin: 'top center' }}>
          {prepared.roots.map(root => renderNode(root))}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap', borderTop: `1px solid ${C.border}`, paddingTop: '12px' }}>
        {Object.entries(prepared.levels).sort(([a], [b]) => Number(a) - Number(b)).map(([level, count]) => (
          <span key={level} style={{ color: C.accentDark, fontSize: '12px', background: 'rgba(158,175,172,0.05)', border: `1px solid ${C.border}`, borderRadius: '999px', padding: '4px 9px' }}>Nivel {level}: {count}</span>
        ))}
      </div>
    </div>
  );
};

const UserDetailPanel = ({ userId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(true);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await axios.get(`${API}/admin/users/${userId}/detail`, { headers: getAdminHeaders() });
        setDetail(res.data);
      } catch { } finally { setLoadingDetail(false); }
    };
    fetchDetail();
  }, [userId]);

  if (loadingDetail) return (
    <div style={{ padding: '20px', textAlign: 'center', color: C.accentDark }}>
      <div style={{ width: '24px', height: '24px', border: `2px solid ${C.accent}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
    </div>
  );

  if (!detail) return null;

  const { user, referrer_name, referrals, active_referrals_count, total_referrals_count, earnings, withdrawals_count, total_withdrawn, last_withdrawal } = detail;

  const row = (label, value, icon) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
      <span style={{ color: C.accentDark, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        {icon && React.createElement(icon, { size: 12 })} {label}
      </span>
      <span style={{ color: 'white', fontSize: '13px', fontWeight: 500 }}>{value}</span>
    </div>
  );

  return (
    <div style={{
      background: '#0d0e0d',
      border: `1px solid ${C.border}`,
      borderRadius: '12px',
      padding: '20px',
      marginTop: '8px',
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {/* Columna izq */}
        <div>
          <div style={{ color: C.accentDark, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '10px' }}>Información del usuario</div>
          {row('Celular', user.phone_e164 || 'No registrado', Phone)}
          {row('Email', user.email || 'No registrado')}
          {row('Registro', user.created_at ? new Date(user.created_at).toLocaleDateString('es-ES') : '-', Calendar)}
          {row('Estado inversión', <StatusBadge status={user.investment_status} />)}
          {row('Referido por', referrer_name || 'Directo')}
        </div>
        {/* Columna der */}
        <div>
          <div style={{ color: C.accentDark, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '10px' }}>Estadísticas</div>
          {row('Afiliados totales', total_referrals_count)}
          {row('Afiliados activos', <span style={{ color: C.green, fontWeight: 700 }}>{active_referrals_count}</span>)}
          {row('Ganancias totales', `$${parseFloat(user.total_earnings || 0).toFixed(2)}`)}
          {row('Retiros realizados', withdrawals_count)}
          {row('Total retirado', `$${total_withdrawn.toFixed(2)}`)}
          {last_withdrawal && row('Último retiro', <StatusBadge status={last_withdrawal.status} />)}
        </div>
      </div>

      {/* Afiliados recientes */}
      {referrals.length > 0 && (
        <div style={{ marginTop: '14px' }}>
          <div style={{ color: C.accentDark, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>Afiliados recientes</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {referrals.slice(0, 6).map(r => (
              <span key={r.id} style={{
                padding: '3px 10px',
                borderRadius: '50px',
                background: r.investment_status === 'active' ? 'rgba(74,222,128,0.1)' : 'rgba(158,175,172,0.06)',
                color: r.investment_status === 'active' ? C.green : C.accentDark,
                fontSize: '11px',
                border: `1px solid ${r.investment_status === 'active' ? 'rgba(74,222,128,0.2)' : C.border}`,
              }}>
                {r.full_name}
              </span>
            ))}
            {referrals.length > 6 && <span style={{ color: C.accentDark, fontSize: '11px', padding: '3px 8px' }}>+{referrals.length - 6} más</span>}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────

export default function AdminPage() {
  const [adminToken, setAdminToken] = useState(localStorage.getItem(ADMIN_TOKEN_KEY));
  const [section, setSection] = useState('stats');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [manualPayments, setManualPayments] = useState([]);
  const [visualTree, setVisualTree] = useState({ nodes: [] });
  const [financialCycles, setFinancialCycles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [wFilter, setWFilter] = useState('pending');
  const [mFilter, setMFilter] = useState('reported');
  const [treeSearch, setTreeSearch] = useState('');
  const [treeStatus, setTreeStatus] = useState('all');
  const [treeDepth, setTreeDepth] = useState(10);
  const [rootUserId, setRootUserId] = useState('');
  const [cycleStatus, setCycleStatus] = useState('all');
  const [expandedCycle, setExpandedCycle] = useState(null);
  const [cycleDetails, setCycleDetails] = useState({});
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  // Password reset state
  const [resetSelectedUser, setResetSelectedUser] = useState(null);
  const [resetAllUsers, setResetAllUsers] = useState([]);
  const [resetSearch, setResetSearch] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetCopied, setResetCopied] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers: getAdminHeaders() });
      setStats(res.data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem(ADMIN_TOKEN_KEY); setAdminToken(null); }
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const f = filter === 'all' ? '' : filter;
      const res = await axios.get(`${API}/admin/users${f ? `?filter=${f}` : ''}`, { headers: getAdminHeaders() });
      setUsers(res.data);
    } finally { setLoading(false); }
  }, [filter]);

  const fetchWithdrawals = useCallback(async () => {
    setLoading(true);
    try {
      const f = wFilter === 'all' ? '' : wFilter;
      const res = await axios.get(`${API}/admin/withdrawals${f ? `?status=${f}` : ''}`, { headers: getAdminHeaders() });
      setWithdrawals(res.data);
    } finally { setLoading(false); }
  }, [wFilter]);

  const fetchManualPayments = useCallback(async () => {
    setLoading(true);
    try {
      const f = mFilter === 'all' ? '' : mFilter;
      const res = await axios.get(`${API}/admin/manual-payments${f ? `?status=${f}` : ''}`, { headers: getAdminHeaders() });
      setManualPayments(res.data);
    } finally { setLoading(false); }
  }, [mFilter]);

  const fetchVisualTree = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('max_depth', String(treeDepth || 10));
      params.set('status', treeStatus || 'all');
      if (rootUserId) params.set('root_user_id', rootUserId);
      const res = await axios.get(`${API}/admin/visual-tree?${params.toString()}`, { headers: getAdminHeaders() });
      setVisualTree(res.data || { nodes: [] });
    } finally { setLoading(false); }
  }, [rootUserId, treeDepth, treeStatus]);

  const fetchFinancialCycles = useCallback(async () => {
    setLoading(true);
    try {
      const f = cycleStatus === 'all' ? '' : `?status=${cycleStatus}`;
      const res = await axios.get(`${API}/admin/financial-cycles${f}`, { headers: getAdminHeaders() });
      setFinancialCycles(res.data || []);
    } finally { setLoading(false); }
  }, [cycleStatus]);

  useEffect(() => { if (adminToken) fetchStats(); }, [adminToken, fetchStats]);
  useEffect(() => { if (adminToken && section === 'users') fetchUsers(); }, [adminToken, section, filter, fetchUsers]);
  useEffect(() => { if (adminToken && section === 'withdrawals') fetchWithdrawals(); }, [adminToken, section, wFilter, fetchWithdrawals]);
  useEffect(() => { if (adminToken && section === 'manual-payments') fetchManualPayments(); }, [adminToken, section, mFilter, fetchManualPayments]);
  useEffect(() => {
    if (adminToken && section === 'visual-tree') {
      fetchVisualTree();
      if (users.length === 0) fetchUsers();
    }
  }, [adminToken, section, fetchVisualTree, fetchUsers, users.length]);
  useEffect(() => { if (adminToken && section === 'financial-cycles') fetchFinancialCycles(); }, [adminToken, section, fetchFinancialCycles]);

  // Load all users for password reset selector
  useEffect(() => {
    if (adminToken && section === 'password-reset') {
      axios.get(`${API}/admin/users`, { headers: getAdminHeaders() })
        .then(res => setResetAllUsers(res.data || []))
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, section]);

  const generateResetLink = async () => {
    if (!resetSelectedUser) return;
    setResetLoading(true);
    setGeneratedLink('');
    try {
      const res = await axios.post(`${API}/admin/users/${resetSelectedUser.id}/password-reset-link`, {}, { headers: getAdminHeaders() });
      setGeneratedLink(res.data.reset_link);
    } catch (err) {
      setActionMsg(err.response?.data?.detail || 'Error generando link');
    } finally {
      setResetLoading(false);
    }
  };

  const copyResetLink = () => {
    navigator.clipboard.writeText(generatedLink);
    setResetCopied(true);
    setTimeout(() => setResetCopied(false), 3000);
  };

  const whatsappResetLink = () => {
    const phone = resetSelectedUser?.phone_e164?.replace('+', '') || '';
    const msg = encodeURIComponent(`Hola ${resetSelectedUser?.full_name}, aquí está tu enlace para cambiar tu contraseña de VaultGrow (válido 30 min):\n\n${generatedLink}`);
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true); setLoginError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(ADMIN_TOKEN_KEY, res.data.token);
      setAdminToken(res.data.token);
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Acceso denegado');
    } finally { setLoginLoading(false); }
  };

  const handleLogout = () => { localStorage.removeItem(ADMIN_TOKEN_KEY); setAdminToken(null); };

  const handleWithdrawalAction = async (id, action) => {
    try {
      const res = await axios.patch(`${API}/admin/withdrawals/${id}`, { action }, { headers: getAdminHeaders() });
      setActionMsg(res.data.message);
      setTimeout(() => setActionMsg(''), 4000);
      fetchWithdrawals(); fetchStats();
    } catch (err) { setActionMsg(err.response?.data?.detail || 'Error al procesar'); }
  };

  const handleManualPaymentAction = async (id, action) => {
    const notes = window.prompt('Nota administrativa (opcional):', '') || '';
    try {
      const res = await axios.patch(`${API}/admin/manual-payments/${id}`, { action, notes }, { headers: getAdminHeaders() });
      setActionMsg(res.data.message);
      setTimeout(() => setActionMsg(''), 4000);
      fetchManualPayments(); fetchStats();
    } catch (err) { setActionMsg(err.response?.data?.detail || 'Error al procesar pago manual'); }
  };

  const toggleUserExpand = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  const toggleCycleExpand = async (cycleId) => {
    if (expandedCycle === cycleId) {
      setExpandedCycle(null);
      return;
    }
    setExpandedCycle(cycleId);
    if (!cycleDetails[cycleId]) {
      const res = await axios.get(`${API}/admin/financial-cycle/${cycleId}`, { headers: getAdminHeaders() });
      setCycleDetails(prev => ({ ...prev, [cycleId]: res.data }));
    }
  };

  // ── Login Screen ─────────────────────────────────────────────────────────────
  if (!adminToken) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <form onSubmit={handleLogin} style={{ width: '100%', maxWidth: '400px', background: C.card, border: `1px solid ${C.border}`, borderRadius: '20px', padding: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
            <Shield size={22} color={C.accent} />
            <h1 style={{ margin: 0, color: 'white', fontFamily: 'Outfit, sans-serif', fontSize: '22px' }}>Panel Administrativo</h1>
          </div>
          <div style={{ position: 'relative', marginBottom: '12px' }}>
            <input
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Contraseña de administrador"
              required
              style={{ width: '100%', background: 'rgba(158,175,172,0.04)', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '13px 42px 13px 14px', color: 'white', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
            />
            <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: 12, top: 12, background: 'none', border: 'none', color: C.accentDark, cursor: 'pointer' }}>
              {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {loginError && <div style={{ color: '#f87171', marginBottom: '10px', fontSize: '14px' }}>{loginError}</div>}
          <button type="submit" disabled={loginLoading} style={{ width: '100%', padding: '13px', border: 'none', borderRadius: '10px', background: C.accent, color: '#131413', fontWeight: 700, fontSize: '15px', cursor: loginLoading ? 'not-allowed' : 'pointer', fontFamily: 'Outfit, sans-serif' }}>
            {loginLoading ? 'Validando...' : 'Ingresar'}
          </button>
        </form>
      </div>
    );
  }

  // ── Helper: tabs ──────────────────────────────────────────────────────────────
  const tab = (key, label, count) => (
    <button
      key={key}
      onClick={() => setSection(key)}
      style={{
        padding: '8px 16px',
        borderRadius: '8px',
        border: 'none',
        background: section === key ? C.accent : 'transparent',
        color: section === key ? '#131413' : C.accentDark,
        fontWeight: 700,
        cursor: 'pointer',
        fontSize: '13px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        transition: 'all 0.15s',
      }}
    >
      {label}
      {count > 0 && (
        <span style={{ background: '#ef4444', color: 'white', borderRadius: '999px', padding: '1px 7px', fontSize: '11px' }}>
          {count}
        </span>
      )}
    </button>
  );

  const filterBtn = (val, label, current, setter) => (
    <button
      key={val}
      onClick={() => setter(val)}
      style={{
        background: current === val ? C.accent : 'rgba(158,175,172,0.06)',
        border: `1px solid ${current === val ? C.accent : C.border}`,
        color: current === val ? '#131413' : C.accentDark,
        padding: '6px 14px',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '12px',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: 'white', fontFamily: 'Inter, sans-serif' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse-dot { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes fadeInDown { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      {/* Navbar admin */}
      <div style={{ background: C.cardAlt, borderBottom: `1px solid ${C.border}`, padding: '12px 24px', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <Shield size={18} color={C.accent} style={{ marginRight: '6px' }} />
          {tab('stats', 'Dashboard')}
          {tab('users', 'Usuarios')}
          {tab('visual-tree', 'Árbol visual')}
          {tab('financial-cycles', 'Ciclos financieros')}
          {tab('withdrawals', 'Retiros', stats?.pending_withdrawals_count || 0)}
          {tab('manual-payments', 'Pagos manuales', stats?.pending_manual_payments_count || 0)}
          {tab('password-reset', 'Cambio de clave', 0)}
          <button
            onClick={handleLogout}
            style={{ marginLeft: 'auto', border: '1px solid rgba(248,113,113,0.2)', background: 'rgba(248,113,113,0.08)', color: '#f87171', borderRadius: '8px', padding: '6px 14px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}
          >
            Salir
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px' }}>
        {actionMsg && (
          <div style={{ marginBottom: '14px', background: 'rgba(158,175,172,0.08)', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 16px', color: C.accent, fontSize: '14px', animation: 'fadeInDown 0.3s ease' }}>
            {actionMsg}
          </div>
        )}

        {/* ── STATS SECTION ─────────────────────────────────────────────────────── */}
        {section === 'stats' && stats && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '22px', fontWeight: 700 }}>Dashboard Operativo</h2>
              <button
                onClick={fetchStats}
                style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '8px', color: C.accentDark, padding: '6px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}
              >
                <RefreshCw size={13} /> Actualizar
              </button>
            </div>

            {/* Alertas operativas */}
            {(stats.pending_withdrawals_count > 0 || stats.pending_manual_payments_count > 0) && (
              <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {stats.pending_withdrawals_count > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.25)', borderRadius: '10px', padding: '12px 16px' }}>
                    <Bell size={15} color={C.orange} />
                    <span style={{ color: C.orange, fontSize: '13px', fontWeight: 600 }}>
                      {stats.pending_withdrawals_count} retiro{stats.pending_withdrawals_count !== 1 ? 's' : ''} pendiente{stats.pending_withdrawals_count !== 1 ? 's' : ''} de procesar · ${stats.pending_withdrawals_amount?.toFixed(2)} en cola
                    </span>
                    <button onClick={() => setSection('withdrawals')} style={{ marginLeft: 'auto', background: 'rgba(251,146,60,0.12)', border: '1px solid rgba(251,146,60,0.3)', borderRadius: '6px', color: C.orange, padding: '4px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>Ver retiros</button>
                  </div>
                )}
                {stats.pending_manual_payments_count > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.25)', borderRadius: '10px', padding: '12px 16px' }}>
                    <Bell size={15} color={C.blue} />
                    <span style={{ color: C.blue, fontSize: '13px', fontWeight: 600 }}>
                      {stats.pending_manual_payments_count} pago{stats.pending_manual_payments_count !== 1 ? 's' : ''} manual{stats.pending_manual_payments_count !== 1 ? 'es' : ''} pendiente{stats.pending_manual_payments_count !== 1 ? 's' : ''} de verificar
                    </span>
                    <button onClick={() => setSection('manual-payments')} style={{ marginLeft: 'auto', background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.3)', borderRadius: '6px', color: C.blue, padding: '4px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>Ver pagos</button>
                  </div>
                )}
              </div>
            )}

            {/* Cards principales */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '20px' }}>
              <StatCard label="Total Usuarios" value={stats.total_users} Icon={Users} />
              <StatCard label="Inversiones Activas" value={stats.active_users} sub={`$${stats.total_invested?.toFixed(0)} invertidos`} Icon={TrendingUp} />
              <StatCard label="Ganancias Pagadas" value={`$${stats.total_earnings_paid?.toFixed(2)}`} Icon={DollarSign} accent={C.gold} />
              <StatCard label="Retiros Pendientes" value={stats.pending_withdrawals_count} sub={`$${stats.pending_withdrawals_amount?.toFixed(2)} en cola`} Icon={Clock} accent={stats.pending_withdrawals_count > 0 ? C.orange : C.accent} alert={stats.pending_withdrawals_count > 0} />
              <StatCard label="Pagos Manuales Pendientes" value={stats.pending_manual_payments_count || 0} Icon={AlertTriangle} accent={stats.pending_manual_payments_count > 0 ? C.orange : C.accent} alert={stats.pending_manual_payments_count > 0} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '20px' }}>
              <StatCard label="Ciclos Abiertos" value={stats.open_matrix_cycles || 0} Icon={Activity} accent={C.accent} />
              <StatCard label="Ciclos Completos" value={stats.completed_matrix_cycles || 0} Icon={CheckCircle} accent={C.green} />
              <StatCard label="Volumen Matriz" value={`$${parseFloat(stats.matrix_gross_volume || 0).toFixed(2)}`} Icon={CreditCard} accent={C.accent} />
              <StatCard label="Ganancias Usuarios" value={`$${parseFloat(stats.matrix_user_earnings || 0).toFixed(2)}`} Icon={DollarSign} accent={C.green} />
              <StatCard label="Ingresos Organización" value={`$${parseFloat(stats.organization_revenue || 0).toFixed(2)}`} Icon={Building2} accent={C.gold} />
            </div>

            {/* Métricas de hoy */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '20px' }}>
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '16px', padding: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Activity size={14} color={C.green} />
                  <span style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Hoy</span>
                </div>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div>
                    <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '24px', fontWeight: 700, color: C.green }}>{stats.new_users_today || 0}</div>
                    <div style={{ color: C.accentDark, fontSize: '11px' }}>Nuevos usuarios</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '24px', fontWeight: 700, color: C.green }}>{stats.new_referrals_today || 0}</div>
                    <div style={{ color: C.accentDark, fontSize: '11px' }}>Referidos activados</div>
                  </div>
                </div>
              </div>

              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '16px', padding: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <UserCheck size={14} color={C.accent} />
                  <span style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Sin referidos directos</span>
                </div>
                <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '24px', fontWeight: 700, color: C.accent }}>{stats.active_no_referrals}</div>
                <div style={{ color: C.accentDark, fontSize: '11px' }}>Activos sin invitados directos</div>
              </div>

              {/* Último retiro pendiente */}
              {stats.last_withdrawal && (
                <div style={{ background: C.card, border: `1px solid rgba(251,146,60,0.2)`, borderRadius: '16px', padding: '18px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <Clock size={14} color={C.orange} />
                    <span style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Último retiro pendiente</span>
                  </div>
                  <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '22px', fontWeight: 700, color: C.orange }}>${parseFloat(stats.last_withdrawal.amount || 0).toFixed(2)}</div>
                  <div style={{ color: C.accentDark, fontSize: '11px', marginTop: '2px' }}>{stats.last_withdrawal.user_name || 'Usuario'}</div>
                  {stats.last_withdrawal.user_phone && <div style={{ color: C.accentDark, fontSize: '11px' }}>{stats.last_withdrawal.user_phone}</div>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── USERS SECTION ─────────────────────────────────────────────────────── */}
        {section === 'users' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <h2 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '20px' }}>Usuarios ({users.length})</h2>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {[['all', 'Todos'], ['active', 'Activos'], ['no_referrals', 'Sin Referidos'], ['pending', 'Pendientes']].map(([v, l]) => filterBtn(v, l, filter, setFilter))}
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: C.accentDark }}>Cargando...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {users.map(u => (
                  <div key={u.id}>
                    <div
                      style={{
                        background: C.card,
                        border: `1px solid ${C.border}`,
                        borderRadius: expandedUser === u.id ? '12px 12px 0 0' : '12px',
                        padding: '14px 16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px',
                        flexWrap: 'wrap',
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                      }}
                      onClick={() => toggleUserExpand(u.id)}
                    >
                      <div style={{ flex: 1, minWidth: '180px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <strong style={{ color: 'white', fontSize: '14px' }}>{u.full_name}</strong>
                          {u.phone_e164 && (
                            <span style={{ color: C.accentDark, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <Phone size={11} /> +{u.phone_e164.replace('+', '').slice(0,3)} - {u.phone_e164.replace('+593', '')}
                            </span>
                          )}
                          {u.is_admin && <span style={{ color: C.gold, fontSize: '11px', background: 'rgba(201,168,76,0.1)', padding: '1px 8px', borderRadius: '50px', border: '1px solid rgba(201,168,76,0.2)' }}>Admin</span>}
                        </div>
                        <div style={{ color: C.accentDark, fontSize: '12px', marginTop: '2px' }}>
                          #{u.user_number || '-'} · {u.referral_code} · Sponsor: {u.sponsor_name || u.referred_by_name || 'Directo'} · Directos activos: {u.direct_active_referrals || 0} · Ciclo financiero: {u.matrix_positions_count || 0}/14 · Volumen: ${parseFloat(u.matrix_gross_volume || 0).toFixed(2)}
                          {(u.direct_active_referrals || 0) >= 2 && (u.matrix_positions_count || 0) === 0 && (
                            <span style={{ color: C.green }}> · Primer nivel completo · ciclo en espera</span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <StatusBadge status={u.investment_status} />
                        <span style={{ color: C.accentDark }}>
                          {expandedUser === u.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </span>
                      </div>
                    </div>
                    {expandedUser === u.id && <UserDetailPanel userId={u.id} onClose={() => setExpandedUser(null)} />}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── WITHDRAWALS SECTION ───────────────────────────────────────────────── */}
        {section === 'visual-tree' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <h2 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '20px' }}>Árbol visual</h2>
              <button onClick={fetchVisualTree} style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '8px', color: C.accentDark, padding: '6px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                <RefreshCw size={13} /> Actualizar
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 2fr) minmax(180px, 1fr) 140px minmax(220px, 1fr)', gap: '10px', marginBottom: '14px' }}>
              <input value={treeSearch} onChange={e => setTreeSearch(e.target.value)} placeholder="Buscar nombre, teléfono, email o código" style={{ background: 'rgba(158,175,172,0.05)', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '10px 12px', color: 'white', outline: 'none' }} />
              <select value={treeStatus} onChange={e => setTreeStatus(e.target.value)} style={{ background: '#0d0e0d', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '10px 12px', color: C.accent }}>
                <option value="all">Todos</option>
                <option value="active">Activos</option>
                <option value="pending">Pendientes</option>
              </select>
              <input type="number" min="1" max="50" value={treeDepth} onChange={e => setTreeDepth(e.target.value)} style={{ background: 'rgba(158,175,172,0.05)', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '10px 12px', color: 'white', outline: 'none' }} />
              <select value={rootUserId} onChange={e => setRootUserId(e.target.value)} style={{ background: '#0d0e0d', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '10px 12px', color: C.accent }}>
                <option value="">Usuarios raíz</option>
                {users.slice(0, 80).map(u => <option key={u.id} value={u.id}>{u.full_name} · {u.referral_code}</option>)}
              </select>
            </div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: C.accentDark }}>Cargando árbol...</div>
            ) : (
              <GraphicalReferralTree
                nodes={visualTree.nodes || []}
                users={users}
                rootUserId={rootUserId}
                search={treeSearch}
                maxDepth={treeDepth}
                status={treeStatus}
                onSelectRoot={setRootUserId}
                onLoadUsers={fetchUsers}
              />
            )}
          </div>
        )}

        {section === 'financial-cycles' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <h2 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '20px' }}>Ciclos financieros</h2>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>{[['all', 'Todos'], ['open', 'Abiertos'], ['completed', 'Completos']].map(([v, l]) => filterBtn(v, l, cycleStatus, setCycleStatus))}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '16px' }}>
              <StatCard label="Ciclos Abiertos" value={stats?.open_matrix_cycles || 0} Icon={Activity} />
              <StatCard label="Ciclos Completos" value={stats?.completed_matrix_cycles || 0} Icon={CheckCircle} accent={C.green} />
              <StatCard label="Volumen Bruto" value={`$${parseFloat(stats?.matrix_gross_volume || 0).toFixed(2)}`} Icon={CreditCard} />
              <StatCard label="Ganancias Usuarios" value={`$${parseFloat(stats?.matrix_user_earnings || 0).toFixed(2)}`} Icon={DollarSign} accent={C.green} />
              <StatCard label="Ingresos Organización" value={`$${parseFloat(stats?.organization_revenue || 0).toFixed(2)}`} Icon={Building2} accent={C.gold} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {financialCycles.map(cycle => {
                const detail = cycleDetails[cycle.cycle_id];
                return (
                  <div key={cycle.cycle_id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>
                    <div onClick={() => toggleCycleExpand(cycle.cycle_id)} style={{ padding: '14px 16px', cursor: 'pointer', display: 'grid', gridTemplateColumns: 'minmax(180px, 1.5fr) 120px 1fr 1fr 110px auto', gap: '10px', alignItems: 'center' }}>
                      <div><strong style={{ color: 'white' }}>{cycle.owner_name}</strong><div style={{ color: C.accentDark, fontSize: '12px' }}>{cycle.owner_referral_code}</div></div>
                      <div style={{ color: C.accent, fontWeight: 700 }}>{cycle.paid_positions_count}/{cycle.capacity}</div>
                      <div style={{ height: '8px', background: 'rgba(255,255,255,0.06)', borderRadius: 999, overflow: 'hidden' }}><div style={{ width: `${Math.min(cycle.progress_pct || 0, 100)}%`, height: '100%', background: C.green }} /></div>
                      <div style={{ color: C.green, fontWeight: 700 }}>${parseFloat(cycle.user_earnings || 0).toFixed(2)}</div>
                      <div style={{ color: C.gold, fontWeight: 700 }}>${parseFloat(cycle.organization_revenue || 0).toFixed(2)}</div>
                      <StatusBadge status={cycle.status} />
                    </div>
                    {expandedCycle === cycle.cycle_id && (
                      <div style={{ padding: '0 16px 16px' }}>
                        {(detail?.positions || []).map(pos => (
                          <div key={pos.position_number} style={{ display: 'grid', gridTemplateColumns: '70px 1fr 100px 110px 110px 150px', gap: '10px', padding: '9px 0', borderTop: `1px solid ${C.border}`, alignItems: 'center', fontSize: '12px' }}>
                            <span style={{ color: C.accent, fontWeight: 800 }}>#{pos.position_number}</span>
                            <span style={{ color: 'white' }}>{pos.payer_name || 'Pendiente'} · {pos.payer_referral_code || '-'}</span>
                            <span style={{ color: C.accentDark }}>depth {pos.visual_depth || 0}</span>
                            <span style={{ color: C.green }}>${parseFloat(pos.user_share || (pos.position_number === 7 || pos.position_number === 14 ? 42.90 : 42.85)).toFixed(2)} usuario</span>
                            <span style={{ color: C.gold }}>${parseFloat(pos.organization_share || (pos.position_number === 7 || pos.position_number === 14 ? 57.10 : 57.15)).toFixed(2)} org.</span>
                            <span style={{ color: C.accentDark }}>{pos.assigned_at ? new Date(pos.assigned_at).toLocaleString('es-ES') : '-'}</span>
                            {pos.position_number === 7 && <span style={{ color: C.green, gridColumn: '1 / -1' }}>Retiro disponible: $300</span>}
                            {pos.position_number === 14 && <span style={{ color: C.green, gridColumn: '1 / -1' }}>Ciclo completo: $600</span>}
                          </div>
                        ))}
                        {!detail && <div style={{ padding: '18px', color: C.accentDark }}>Cargando posiciones...</div>}
                      </div>
                    )}
                  </div>
                );
              })}
              {!loading && financialCycles.length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: C.accentDark }}>No hay ciclos para este filtro</div>}
            </div>
          </div>
        )}

        {section === 'withdrawals' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <h2 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '20px' }}>Retiros</h2>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {[['pending', 'Pendientes'], ['processing', 'Procesando'], ['completed', 'Completados'], ['rejected', 'Rechazados'], ['all', 'Todos']].map(([v, l]) => filterBtn(v, l, wFilter, setWFilter))}
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: C.accentDark }}>Cargando...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }} data-testid="admin-withdrawals-list">
                {withdrawals.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px', color: C.accentDark }}>No hay retiros con este estado</div>
                )}
                {withdrawals.map(w => (
                  <div key={w.id} style={{ background: C.card, border: `1px solid ${w.status === 'pending' ? 'rgba(251,146,60,0.25)' : C.border}`, borderRadius: '12px', padding: '16px' }}>
                    {/* Header del retiro */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <strong style={{ color: 'white', fontSize: '15px' }}>{w.user_name}</strong>
                          {w.user_phone && (
                            <span style={{ color: C.accentDark, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <Phone size={11} /> +{w.user_phone.replace('+', '').slice(0,3)} - {w.user_phone.replace('+593', '')}
                            </span>
                          )}
                        </div>
                        <div style={{ color: C.accentDark, fontSize: '12px', marginTop: '2px' }}>
                          {w.requested_at ? new Date(w.requested_at).toLocaleString('es-ES') : '-'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                        <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '20px', fontWeight: 700, color: C.accent }}>${parseFloat(w.amount).toFixed(2)}</div>
                        <StatusBadge status={w.status} />
                      </div>
                    </div>

                    {/* Datos del método */}
                    <div style={{ background: 'rgba(158,175,172,0.04)', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                      {w.withdrawal_method === 'bank_transfer' ? (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                            <Building2 size={13} color={C.accent} />
                            <span style={{ color: C.accent, fontSize: '12px', fontWeight: 700 }}>Transferencia Bancaria</span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '6px' }}>
                            {[
                              ['Banco', w.bank_name],
                              ['Tipo de cuenta', w.bank_account_type],
                              ['Titular', w.bank_account_holder],
                              ['Identificación', w.bank_account_id_number],
                              ['Número de cuenta', w.bank_account_number],
                            ].map(([label, val]) => val && (
                              <div key={label} style={{ background: 'rgba(158,175,172,0.04)', borderRadius: '6px', padding: '6px 10px' }}>
                                <div style={{ color: C.accentDark, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
                                <div style={{ color: 'white', fontSize: '13px', fontWeight: 500, marginTop: '2px' }}>{val}</div>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                            <Bitcoin size={13} color={C.accent} />
                            <span style={{ color: C.accent, fontSize: '12px', fontWeight: 700 }}>Criptomonedas · {w.network || 'TRC20'}</span>
                          </div>
                          <div style={{ background: 'rgba(158,175,172,0.04)', borderRadius: '6px', padding: '8px 10px' }}>
                            <div style={{ color: C.accentDark, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Wallet Address ({w.network || 'TRC20'})</div>
                            <code style={{ color: 'white', fontSize: '13px', wordBreak: 'break-all', fontFamily: 'monospace' }}>{w.wallet_address}</code>
                          </div>
                        </>
                      )}
                      {w.notes && (
                        <div style={{ marginTop: '8px', color: C.accentDark, fontSize: '12px' }}>Notas: {w.notes}</div>
                      )}
                    </div>

                    {/* Acciones */}
                    {w.status === 'pending' && (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <ActionBtn onClick={() => handleWithdrawalAction(w.id, 'processing')} color={C.blue} bg="rgba(96,165,250,0.08)" border="rgba(96,165,250,0.25)">Marcar en Proceso</ActionBtn>
                        <ActionBtn onClick={() => handleWithdrawalAction(w.id, 'approve')} data-testid={`approve-withdrawal-${w.id}`} color={C.green} bg="rgba(74,222,128,0.08)" border="rgba(74,222,128,0.25)">Aprobar</ActionBtn>
                        <ActionBtn onClick={() => handleWithdrawalAction(w.id, 'reject')} data-testid={`reject-withdrawal-${w.id}`} color={C.red} bg="rgba(248,113,113,0.08)" border="rgba(248,113,113,0.25)">Rechazar</ActionBtn>
                      </div>
                    )}
                    {w.status === 'processing' && (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <ActionBtn onClick={() => handleWithdrawalAction(w.id, 'approve')} color={C.green} bg="rgba(74,222,128,0.08)" border="rgba(74,222,128,0.25)">Marcar Completado</ActionBtn>
                        <ActionBtn onClick={() => handleWithdrawalAction(w.id, 'reject')} color={C.red} bg="rgba(248,113,113,0.08)" border="rgba(248,113,113,0.25)">Rechazar</ActionBtn>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── MANUAL PAYMENTS SECTION ───────────────────────────────────────────── */}
        {section === 'manual-payments' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <h2 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '20px' }}>Pagos Manuales</h2>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {[['reported', 'Reportados'], ['finished', 'Aprobados'], ['failed', 'Rechazados'], ['all', 'Todos']].map(([v, l]) => filterBtn(v, l, mFilter, setMFilter))}
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: C.accentDark }}>Cargando...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {manualPayments.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px', color: C.accentDark }}>No hay pagos con este estado</div>
                )}
                {manualPayments.map(p => (
                  <div key={p.id} style={{ background: C.card, border: `1px solid ${p.status === 'reported' ? 'rgba(96,165,250,0.25)' : C.border}`, borderRadius: '12px', padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <strong style={{ color: 'white', fontSize: '15px' }}>{p.user_name}</strong>
                          {p.user_phone && (
                            <span style={{ color: C.accentDark, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <Phone size={11} /> +{p.user_phone.replace('+', '').slice(0,3)} - {p.user_phone.replace('+593', '')}
                            </span>
                          )}
                        </div>
                        <div style={{ color: C.accentDark, fontSize: '12px', marginTop: '2px' }}>
                          {new Date(p.manual_reported_at || p.created_at).toLocaleString('es-ES')}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                        <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '20px', fontWeight: 700, color: C.accent }}>${parseFloat(p.amount).toFixed(2)}</div>
                        <StatusBadge status={p.status} />
                      </div>
                    </div>
                    {p.admin_notes && (
                      <div style={{ color: C.accentDark, fontSize: '12px', marginBottom: '10px' }}>Notas: {p.admin_notes}</div>
                    )}
                    {(p.status === 'reported' || p.status === 'pending') && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <ActionBtn onClick={() => handleManualPaymentAction(p.id, 'approve')} color={C.green} bg="rgba(74,222,128,0.08)" border="rgba(74,222,128,0.25)">Aprobar</ActionBtn>
                        <ActionBtn onClick={() => handleManualPaymentAction(p.id, 'reject')} color={C.red} bg="rgba(248,113,113,0.08)" border="rgba(248,113,113,0.25)">Rechazar</ActionBtn>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* ── PASSWORD RESET SECTION ───────────────────────────────────────────── */}
        {section === 'password-reset' && (
          <div>
            <div style={{ marginBottom: '20px' }}>
              <h2 style={{ margin: '0 0 6px', fontFamily: 'Outfit, sans-serif', fontSize: '20px' }}>Cambio de Contraseña</h2>
              <p style={{ color: C.accentDark, fontSize: '13px', margin: 0 }}>Genera un enlace seguro para que el usuario cambie su contraseña. El enlace expira en 30 minutos.</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', maxWidth: '900px' }}>
              {/* Left: user selector */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '16px', padding: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <Key size={16} color={C.accent} />
                  <span style={{ color: C.accent, fontWeight: 700, fontSize: '14px' }}>Seleccionar usuario</span>
                </div>

                {/* Search */}
                <input
                  type="text"
                  placeholder="Buscar por nombre o teléfono..."
                  value={resetSearch}
                  onChange={e => { setResetSearch(e.target.value); setResetSelectedUser(null); setGeneratedLink(''); }}
                  style={{ width: '100%', background: 'rgba(158,175,172,0.05)', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '10px 14px', color: 'white', fontSize: '14px', boxSizing: 'border-box', outline: 'none', marginBottom: '10px' }}
                />

                {/* Users list */}
                <div style={{ maxHeight: '280px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {resetAllUsers
                    .filter(u => {
                      if (!resetSearch) return true;
                      const q = resetSearch.toLowerCase();
                      return (u.full_name || '').toLowerCase().includes(q) || (u.phone_e164 || '').includes(q);
                    })
                    .slice(0, 30)
                    .map(u => (
                      <div
                        key={u.id}
                        onClick={() => { setResetSelectedUser(u); setGeneratedLink(''); setResetCopied(false); }}
                        style={{
                          padding: '10px 14px', borderRadius: '10px', cursor: 'pointer',
                          background: resetSelectedUser?.id === u.id ? 'rgba(158,175,172,0.12)' : 'rgba(158,175,172,0.03)',
                          border: `1px solid ${resetSelectedUser?.id === u.id ? C.accent : C.border}`,
                          transition: 'all 0.15s',
                        }}
                      >
                        <div style={{ color: 'white', fontSize: '14px', fontWeight: resetSelectedUser?.id === u.id ? 700 : 400 }}>{u.full_name}</div>
                        <div style={{ color: C.accentDark, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                          <Phone size={10} /> {u.phone_e164 || 'Sin teléfono'} · <StatusBadge status={u.investment_status} />
                        </div>
                      </div>
                    ))
                  }
                  {resetAllUsers.length === 0 && <div style={{ color: C.accentDark, fontSize: '13px', padding: '10px' }}>Cargando usuarios...</div>}
                </div>
              </div>

              {/* Right: generate & share */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '16px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <LinkIcon size={16} color={C.accent} />
                  <span style={{ color: C.accent, fontWeight: 700, fontSize: '14px' }}>Generar y compartir enlace</span>
                </div>

                {resetSelectedUser ? (
                  <>
                    {/* Selected user info */}
                    <div style={{ background: 'rgba(158,175,172,0.05)', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px' }}>
                      <div style={{ color: 'white', fontWeight: 600 }}>{resetSelectedUser.full_name}</div>
                      <div style={{ color: C.accentDark, fontSize: '12px', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Phone size={11} /> {resetSelectedUser.phone_e164 || 'Sin teléfono'}
                      </div>
                    </div>

                    {/* Generate button */}
                    <button
                      onClick={generateResetLink}
                      disabled={resetLoading}
                      style={{ padding: '12px', background: 'rgba(158,175,172,0.1)', border: `1px solid rgba(158,175,172,0.3)`, borderRadius: '10px', color: 'white', fontWeight: 700, cursor: resetLoading ? 'not-allowed' : 'pointer', fontSize: '14px', fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                      <Key size={15} />
                      {resetLoading ? 'Generando...' : 'Generar enlace de cambio de contraseña'}
                    </button>

                    {/* Generated link */}
                    {generatedLink && (
                      <div>
                        <div style={{ background: '#0a0b0a', border: `1px solid rgba(74,222,128,0.25)`, borderRadius: '10px', padding: '12px', marginBottom: '10px', wordBreak: 'break-all' }}>
                          <div style={{ color: '#586060', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>Enlace generado · válido 30 min</div>
                          <code style={{ color: '#4ade80', fontSize: '12px' }}>{generatedLink}</code>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={copyResetLink}
                            style={{ flex: 1, padding: '10px', background: resetCopied ? 'rgba(74,222,128,0.1)' : 'rgba(158,175,172,0.08)', border: `1px solid ${resetCopied ? 'rgba(74,222,128,0.3)' : C.border}`, borderRadius: '10px', color: resetCopied ? '#4ade80' : C.accent, fontWeight: 600, cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontFamily: 'Inter, sans-serif' }}
                          >
                            {resetCopied ? <Check size={14} /> : <Copy size={14} />}
                            {resetCopied ? 'Copiado' : 'Copiar enlace'}
                          </button>
                          {resetSelectedUser.phone_e164 && (
                            <button
                              onClick={whatsappResetLink}
                              style={{ flex: 1, padding: '10px', background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)', borderRadius: '10px', color: '#25D366', fontWeight: 600, cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontFamily: 'Inter, sans-serif' }}
                            >
                              <MessageCircle size={14} />
                              Enviar por WhatsApp
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.accentDark, fontSize: '14px', textAlign: 'center', padding: '40px 0' }}>
                    Selecciona un usuario de la lista para generar su enlace de cambio de contraseña
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
